import { useState, useRef, useEffect } from 'react';
import { Monitor, Laptop, Server, Router, Network, Share2, Power, GripVertical, Trash2, Cable, Settings, Terminal } from 'lucide-react';
import useStore from '../lib/store';

const icons = {
  pc: Monitor,
  laptop: Laptop,
  server: Server,
  router: Router,
  switch: Network,
  hub: Share2,
};

export default function Device({ device }) {
  const [isDragging, setIsDragging] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const hideTimeout = useRef(null);
  
  const selectedDevice = useStore(state => state.selectedDevice);
  const selectDevice = useStore(state => state.selectDevice);
  const updateDevicePosition = useStore(state => state.updateDevicePosition);
  const updateDevice = useStore(state => state.updateDevice);
  const removeDevice = useStore(state => state.removeDevice);
  const connectionStart = useStore(state => state.connectionStart);
  const addConnection = useStore(state => state.addConnection);
  const startConnection = useStore(state => state.startConnection);
  const cancelConnection = useStore(state => state.cancelConnection);
  const setPanelOpen = useStore(state => state.setPanelOpen);
  const setPanelType = useStore(state => state.setPanelType);

  const Icon = icons[device.type] || Monitor;
  const isSelected = selectedDevice?.id === device.id;

  // Generate consistent color for network tag
  const getNetworkTagColor = (tag) => {
    if (!tag) return null;
    
    const colors = [
      '#3b82f6', // blue
      '#8b5cf6', // purple
      '#ec4899', // pink
      '#f59e0b', // amber
      '#10b981', // emerald
      '#06b6d4', // cyan
      '#f97316', // orange
    ];
    
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = tag.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  const handleDragStart = (e) => {
    e.stopPropagation();
    setIsDragging(true);

    dragOffset.current = {
      x: e.clientX - device.x,
      y: e.clientY - device.y,
    };

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  };

  const handleDragMove = (e) => {
    const newX = e.clientX - dragOffset.current.x;
    const newY = e.clientY - dragOffset.current.y;
    updateDevicePosition(device.id, newX, newY);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
    };
  }, []);

  const handleMouseEnter = () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
    }
    setShowControls(true);
  };

  const handleMouseLeave = () => {
    hideTimeout.current = setTimeout(() => {
      if (!isDragging) {
        setShowControls(false);
      }
    }, 300);
  };

  const handleDeviceClick = (e) => {
    e.stopPropagation();
    selectDevice(device);
  };

  const handlePowerToggle = (e) => {
    e.stopPropagation();
    updateDevice(device.id, { powered: !device.powered });
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (confirm(`Delete ${device.name}?`)) {
      removeDevice(device.id);
    }
  };

  const handleOpenConfig = (e) => {
    e.stopPropagation();
    selectDevice(device);
    setPanelType('config');
    setPanelOpen(true);
  };

  const handleOpenTerminal = (e) => {
    e.stopPropagation();
    selectDevice(device);
    setPanelType('terminal');
    setPanelOpen(true);
  };

  const handlePortClick = (portIndex, e) => {
    e.stopPropagation();
    
    if (!connectionStart) {
      startConnection(device.id, portIndex);
    } else {
      if (connectionStart.deviceId !== device.id) {
        addConnection(
          connectionStart.deviceId,
          connectionStart.port,
          device.id,
          portIndex
        );
      } else {
        cancelConnection();
      }
    }
  };

  const visiblePorts = Math.min(device.ports, 8);
  const networkTagColor = getNetworkTagColor(device.networkTag);

  return (
    <div
      className="network-device-wrapper"
      style={{ 
        left: device.x, 
        top: device.y,
        position: 'absolute',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Controls positioned ABOVE the device card */}
      <div 
        className={`device-controls ${showControls || isSelected ? 'visible' : ''}`}
        onMouseEnter={handleMouseEnter}
      >
        <button 
          className="device-control-button drag-handle" 
          onMouseDown={handleDragStart}
          title="Drag to move"
        >
          <GripVertical size={14} />
        </button>

        <button 
          className="device-control-button terminal-button"
          onClick={handleOpenTerminal}
          title="Open terminal"
        >
          <Terminal size={14} />
        </button>

        <button 
          className="device-control-button config-button"
          onClick={handleOpenConfig}
          title="Configure device"
        >
          <Settings size={14} />
        </button>

        <button 
          className="device-control-button delete-button"
          onClick={handleDelete}
          title="Delete device"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Main Device Card - Compact */}
      <div
        className={`network-device ${isSelected ? 'selected' : ''} ${device.powered ? 'powered-on' : ''} ${isDragging ? 'dragging' : ''}`}
        style={{ 
          transition: isDragging ? 'none' : 'box-shadow 0.2s, border-color 0.2s'
        }}
        onClick={handleDeviceClick}
      >
        <div className="device-header">
          <Icon className="device-icon" size={20} />
          
          <button
            className={`device-power ${device.powered ? 'powered' : ''}`}
            onClick={handlePowerToggle}
            title={device.powered ? 'Power off' : 'Power on'}
          >
            <Power size={14} />
          </button>
        </div>

        <div className="device-name">{device.name}</div>
        <div className="device-ip">
          {device.type === 'router' 
            ? (device.interfaces?.[0]?.ip || 'Not configured')
            : (device.ip || 'Not configured')
          }
        </div>

        {/* Simple Ethernet Ports */}
        <div className="device-ports-simple">
          {Array.from({ length: visiblePorts }, (_, i) => (
            <button
              key={i}
              className={`port-simple ${connectionStart?.deviceId === device.id && connectionStart?.port === i ? 'connecting' : ''}`}
              onClick={(e) => handlePortClick(i, e)}
              title={`Port ${i + 1} - Click to connect`}
            >
              <Cable size={16} />
            </button>
          ))}
        </div>

        {/* Network Tag Badge */}
        {device.networkTag && (
          <div 
            className="network-tag-badge"
            style={{ 
              backgroundColor: networkTagColor,
              color: 'white'
            }}
          >
            {device.networkTag}
          </div>
        )}
      </div>
    </div>
  );
}
